import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Cumberland being a new authority help or hurt my claim?',
    answer: 'It can help on evidence grounds. Cumberland Council formed on 1 April 2023 when Allerdale, Carlisle and Copeland merged. The transparency report shows no red/amber/green condition breakdown before 2024, and the council states predecessor data has not been integrated. For incidents from 2023 onwards, demonstrating a long-established, documented inspection system under Section 58 is harder when the authority has limited published condition history and transitional records.',
  },
  {
    question: 'Cumberland\'s DfT condition scorecard is AMBER — does that mean I cannot claim?',
    answer: 'No. AMBER condition means Cumberland performs in the middle band nationally on published network-level data — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The 2024 survey shows 25% of U-roads in RED and 52% in RED or Amber combined. Those figures are council-published evidence about the network your pothole sits on, not a bar to claiming.',
  },
  {
    question: 'What if my pothole was on a U-road?',
    answer: 'Unclassified roads make up 1,970km — 54.3% of Cumberland\'s 3,627km carriageway network. The 2024 survey recorded 25% RED and 27% Amber on U-roads. Cumberland generates U-road condition figures from Vaisala Road AI automated surveys, which the council\'s transparency report states are "susceptible to both lighting and environmental conditions." If your incident was on a residential or estate road, ask what inspection record exists for that specific street.',
  },
  {
    question: 'What if they say we inherited this problem from predecessor authorities?',
    answer: 'Liability for highway maintenance transferred with local government reorganisation. Cumberland assumed responsibility from 1 April 2023. "We inherited it" does not by itself satisfy a Section 58 defence — the council must still show a reasonable system for the road and defect in question. The inherited backlog and missing predecessor records are factual context for your claim, not legal advice on outcome.',
  },
  {
    question: 'What if my claim is from before April 2023?',
    answer: 'Claims for incidents before 1 April 2023 may involve the predecessor authorities (Allerdale, Carlisle or Copeland) rather than Cumberland Council, depending on location and timing. Cumberland\'s transparency report does not publish integrated inspection records from those councils. If your incident falls in the transition period, road classification, exact location and date determine which authority responds.',
  },
  {
    question: 'Cumberland fills more than 8,000 potholes a year — does that mean the roads are fixed?',
    answer: 'Not necessarily for your street. The council reported 8,419 potholes filled in 2023/24 and 8,271 in 2024/25 — the only two years published. That volume shows continuous reactive workload across 3,627km, including 1,025km of U-roads in RED or Amber condition. Aggregate repair counts do not prove your specific defect was known, inspected and repaired within reasonable timeframes.',
  },
  {
    question: 'Is my road maintained by Cumberland or the CNDR PFI contract?',
    answer: '159km of Cumberland\'s network is maintained by Connect Roads under the CNDR PFI contract, leaving 3,468km under direct council management according to the transparency report. If your pothole was on a PFI-maintained route, the responsible maintainer may differ. Your claim pack should identify the correct highway authority for the exact road section.',
  },
  {
    question: 'Why does the four-year condition data gap matter?',
    answer: 'The transparency report lists 0% red/amber/green for A, B/C and U roads from 2020 through 2023, then publishes full 2024 figures — notably 25% RED on U-roads. The council notes it is a new unitary authority established in 2023. For Section 58, a claimant can ask what documented condition and inspection history existed for their road before and immediately after the merger, and whether the 2024 AI survey captured their street.',
  },
  {
    question: 'How do I report a pothole to Cumberland Council?',
    answer: 'Report defects via the council\'s "Report a problem with a street or road" service at cumberland.gov.uk, or call the Highways Hotline on 0300 373 3736 (Monday to Friday, 8.30am to 5pm). The council states potholes are generally actionable at 40mm depth or greater. Prior reports of the same defect can demonstrate notice before your incident — Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function CumberlandCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cumberland Pothole Claims | 25% RED U-Roads, First Survey Since 2023 Merger | Fixtyer"
        description="Cumberland Council earns RED overall DfT ratings. Its first condition survey found 25% of 1,970km U-roads in RED, with no RAG data before 2024. See what that means for your Section 58 claim."
        canonical="/councils/cumberland"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Cumberland', url: '/councils/cumberland' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Cumberland Council"
        councilSlug="cumberland"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/cumberland"
        headline="Cumberland Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Cumberland</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-blue-200 text-sm">
              <strong className="text-blue-400">New authority caveat:</strong> Cumberland Council formed on{' '}
              <strong className="text-white">1 April 2023</strong> from Allerdale, Carlisle and Copeland. The
              transparency report shows no red/amber/green condition data for 2020–2023; the first full breakdown
              appears in <strong className="text-white">2024</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            25% of Cumberland's 1,970km U-Roads in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Cumberland Council — a unitary authority formed in April 2023 — maintains 3,627km of roads. Its first
            published condition survey in 2024 found <strong className="text-rose-400">25% of U-roads in RED</strong>{' '}
            and <strong className="text-amber-400">27% in Amber</strong> — 52% of the 1,970km unclassified network
            requiring attention. The DfT rates Cumberland <strong className="text-rose-400">RED</strong> overall with{' '}
            <strong className="text-amber-400">AMBER</strong> condition, spend and best-practice scorecards. Section 58
            still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">1,025km</div>
            <div className="text-white font-medium mt-2">U-roads in RED or Amber</div>
            <div className="text-brand-300 text-sm mt-1">
              52% of Cumberland's unclassified network — residential streets, estate roads and local routes — at the
              2024 survey. No comparable RAG history exists in the transparency report before that year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            3,627km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Cumberland's June 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">3,627km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">1,970km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">54.3% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,334km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">36.8% of network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">323km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">8.9% of network</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Cumberland highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,925km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">676km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">1,668km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and culverts</td>
                  <td className="px-6 py-4 text-center font-medium">822 bridges, 630 culverts</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">27,570</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Gullies</td>
                  <td className="px-6 py-4 text-center font-medium">72,482</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">The CNDR PFI exception</h3>
            <p className="text-blue-800 text-sm">
              159km of Cumberland's network is maintained by Connect Roads under the CNDR PFI contract. That leaves{' '}
              <strong>3,468km under direct council management</strong>. Claims on PFI-maintained sections may involve
              a different maintainer — verify the exact road before submitting.
            </p>
          </div>

          <QuoteCard
            quote="Cumberland Council is a new unitary authority that established in 2023. It covers the former districts of Allerdale, Carlisle and Copeland."
            source="Cumberland Council Local Highways Maintenance Transparency Report (Incentive Fund Dashboard, June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the 2024 Survey Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            First red/amber/green breakdown in the transparency report — after four years of zeros
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Data caveat:</strong> Condition rows for 2020–2023 show 0% red/amber/green (U-roads show no data
              for 2020–2021). The council states it was formally established on 1 April 2023. Figures for 2025/26
              spending in the transparency report are projected budgets. U-road condition is generated from Vaisala
              Road AI surveys, which the council notes are susceptible to lighting and environmental conditions.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Cumberland road condition by class 2020-2024</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">A-roads</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">B/C roads</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">U-roads</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100 bg-rose-50/50">
                  <td className="px-4 py-3 font-mono">2020</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">No data</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50/50">
                  <td className="px-4 py-3 font-mono">2021</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">No data</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50/50">
                  <td className="px-4 py-3 font-mono">2022</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50/50">
                  <td className="px-4 py-3 font-mono">2023</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                  <td className="px-4 py-3 text-center text-surface-400">0% / 0% / 0%</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-4 py-3 font-mono font-bold">2024</td>
                  <td className="px-4 py-3 text-center font-medium">2% / 21% / 77%</td>
                  <td className="px-4 py-3 text-center font-medium">7% / 33% / 60%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">25% / 27% / 48%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (1,970km) — where most claims start</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">25%</div>
                <div className="text-sm text-rose-800">RED (Poor)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">27%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">48%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-rose-800 font-medium">
              Combined RED + Amber: 52% of U-roads — approximately 1,025km requiring maintenance attention on the
              roads most Cumberland residents use daily.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,334km)</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">7%</div>
                <div className="text-sm text-rose-800">RED</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">33%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">60%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-amber-800 text-sm">
              Combined RED + Amber: 40% — approximately 533km. SCANNER and Vaisala surveys cover B and C roads annually
              per the transparency report.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (323km)</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2%</div>
                <div className="text-sm text-rose-800">RED</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">21%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">77%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are the smallest carriageway class at 8.9% of the network. Some major routes may be maintained by
              National Highways rather than the council.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Network-wide picture at the 2024 survey</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">1,025km</div>
                <div className="text-brand-300 text-sm">U-roads (52% RED+Amber)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">533km</div>
                <div className="text-brand-300 text-sm">B/C roads (40% RED+Amber)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brand-400">74km</div>
                <div className="text-brand-300 text-sm">A-roads (23% RED+Amber)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Combined: <span className="text-rose-400">1,632km</span> in RED or Amber —{' '}
              <span className="text-rose-400">45% of Cumberland's 3,627km network</span> at the first published survey.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            AMBER spend — projected 2025/26 capital spend is below the DfT allocation despite a £37.8m total highways budget
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Cumberland highway maintenance spending</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">34,168</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">26,453</td>
                  <td className="px-4 py-3 text-center">11,337</td>
                  <td className="px-4 py-3 text-center text-emerald-400">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">16,783</td>
                  <td className="px-4 py-3 text-center">19,064</td>
                  <td className="px-4 py-3 text-center">8,170</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">21,195</td>
                  <td className="px-4 py-3 text-center">20,602</td>
                  <td className="px-4 py-3 text-center">7,210</td>
                  <td className="px-4 py-3 text-center">69%</td>
                  <td className="px-4 py-3 text-center">31%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-surface-400">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-500" colSpan={5}>Not reported — pre-merger</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-surface-400">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-500" colSpan={5}>Not reported — pre-merger</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why the preventative split looks strong</h3>
              <p className="text-brand-200 text-sm">
                The council reports 68% preventative and 32% reactive spend projected for 2025/26, with similar splits in
                2023/24 and 2024/25. The transparency report states preventative figures represent scheme spend to extend
                asset life, while reactive figures use revenue budget allocation.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is still AMBER</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend of £26.453m is below the £34.168m DfT allocation — the pattern behind the
                AMBER spend scorecard. Aggregate budgets do not prove the individual pothole on your road was inspected
                and repaired within reasonable timeframes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            8,400 Potholes Filled — Two Years of Data
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — the only years published in the transparency report since the 2023 merger
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Cumberland estimated potholes filled</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,419</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,271</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Continuous reactive workload</h3>
              <p className="text-surface-600 text-sm">
                More than 8,000 pothole fills per year across 3,627km works out to well over 20 repairs daily on average.
                That is consistent with a network where 45% of carriageway length was in RED or Amber at the 2024 survey
                — evidence of ongoing defect formation, not proof your specific road was maintained.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">No trend line before 2023/24</h3>
              <p className="text-rose-800 text-sm">
                The transparency report does not publish pothole fill counts for 2020/21–2022/23. You cannot compare
                year-on-year repair volumes across the merger from council data alone — only these two post-merger figures
                are available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI surveys and inspections */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            AI Surveys, SCANNER and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Cumberland says it knows road condition — and where gaps appear for U-roads
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey methods by road class</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>
                  • <strong>A roads:</strong> SCANNER annual survey (50% both directions or 100% one direction per year;
                  full coverage in two years) plus annual Vaisala condition data
                </li>
                <li>
                  • <strong>B/C roads:</strong> SCANNER annual surveys (B roads 100% one direction per year; C roads 50%
                  one direction — four years for full C-road coverage) plus annual Vaisala data
                </li>
                <li>
                  • <strong>U roads:</strong> Vaisala Road AI automated surveys feeding the Pavement Management System —
                  frequency not specified in the transparency report
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">PAS 2161 from 2026/27</h3>
              <p className="text-surface-600 text-sm mb-3">
                The transparency report notes a new BSI PAS 2161 methodology from 2026/27, with five condition categories
                instead of three. Cumberland assisted DfT accreditation trials on its network. Today's published RAG data
                reflects the current three-category system only.
              </p>
              <p className="text-surface-600 text-sm">
                For claims about defects now, ask what inspection and condition records exist under the current system for
                your specific road section.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Vaisala Road AI is an automated road survey method collecting carriageway condition data that can imported into our PMS (Pavement Management System). The data captured is used to generate our U Roads performance figures."
              source="Cumberland Council Local Highways Maintenance Transparency Report (Incentive Fund Dashboard, June 2025)"
            />
            <QuoteCard
              quote="Although the RoadAI automated analysis and mapping is based on computer vision algorithms trained together with pavement distress specialists and trained road inspectors, as with traditional visually collected condition data, it appears that although this is a more repeatable process, it is still susceptible to both lighting and environmental conditions."
              source="Cumberland Council Local Highways Maintenance Transparency Report (Incentive Fund Dashboard, June 2025)"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Cumberland must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show a
              reasonable system for inspecting and repairing the specific defect — not just publish network-level condition
              tables.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road included in a Vaisala Road AI survey — and when?</li>
              <li>• What manual verification followed the AI assessment on your street?</li>
              <li>• Were there prior reports (council portal, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the last survey snapshot?</li>
              <li>• For pre-2024 incidents, what predecessor-authority inspection records can the council produce?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Inherited liability */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Allerdale, Carlisle, Copeland — What Transferred
          </h2>
          <p className="text-surface-600 mb-8">
            Liability and assets merged in April 2023; published inspection history did not
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">Cumberland inherited</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 3,627km of highway carriageway and associated assets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Maintenance responsibilities from 1 April 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> The duty to keep the network safe and accessible
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A 2024 baseline showing 25% RED U-roads and 45% network-wide RED/Amber
                </li>
              </ul>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">Not integrated in published data</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Predecessor condition trends (2020–2023 show zeros)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Complete inspection records from Allerdale, Carlisle or Copeland
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole fill history before 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Spending rows for 2021/22 and 2022/23
                </li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="The council will maintain the highway network to the best possible standard within the available resources, while continuing to pursue all the opportunities we can to secure additional funding for the maintenance and improvement of our highways and transport infrastructure."
            source="Cumberland Council Local Highways Maintenance Transparency Report (Incentive Fund Dashboard, June 2025)"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated New Authority
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Cumberland's inherited backlog is documented — but expect a structured defence on classified roads
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> DfT condition scorecard is AMBER, not the lowest band
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 68% preventative spend projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> SCANNER plus Vaisala coverage on A and B/C roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads at 77% green and B/C roads at 60% green in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Asset-management restructuring and PAS 2161 trial participation
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence on principal roads with SCANNER history. Generic neglect arguments alone may not suffice.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall DfT rating — one of 13 red-rated authorities nationally
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,970km of U-roads — 54% of network — with 25% RED at the 2024 survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AI U-road surveys acknowledged as susceptible to lighting and environmental conditions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No published RAG condition history before 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 8,419 and 8,271 annual pothole fills — continuous reactive workload
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Projected capital spend below DfT allocation (£26.453m vs £34.168m)
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a new authority with a documented inherited backlog, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, AI survey limitations and the 25% RED figure are your strongest structural arguments</li>
              <li>• Incident date — pre-2024 claims can probe the data void and predecessor record gap</li>
              <li>• PFI status — confirm whether Connect Roads or the council maintains your section</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Cumberland's own
              transparency data where it helps you — without overstating what the published figures prove.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Cumberland Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack. The council states potholes are generally actionable at
            40mm depth or greater.
          </p>
          <a
            href="https://www.cumberland.gov.uk/parking-roads-and-transport/streets-roads-and-pavements/road-maintenance-closures-and-improvements/report-problem-street-or-road"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a problem with a street or road — cumberland.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Highways Hotline: 0300 373 3736 (Monday to Friday, 8.30am to 5pm)
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Cumberland?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A new authority with a documented U-road crisis demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 25% RED U-road evidence</li>
                <li>• No pre-2024 data-void argument</li>
                <li>• No AI survey limitation quotes</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 25% U-roads RED documented</li>
                <li>✅ Four-year condition data void evidenced</li>
                <li>✅ AI survey limitations quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Cumberland</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=cumberland"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Cumberland Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.cumberland.gov.uk/parking-roads-and-transport/highways-strategies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Cumberland Council Local Highways Maintenance Transparency Report (Incentive Fund Dashboard, June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
